import { Graphics } from "pixi.js";
import type { GameConfig } from "../config/GameConfig";
import { clamp } from "../core/math";
import type { Divider } from "../entities/Divider";

interface Spark {
  x: number;
  y: number;
  age: number;
  life: number;
  hue: number;
  radius: number;
}

export class MouseTrail {
  readonly view = new Graphics();
  private readonly sparks: Spark[] = [];
  private lastX = Number.NaN;
  private lastY = Number.NaN;
  private hue = 28;

  constructor(private readonly config: GameConfig) {
    this.view.eventMode = "none";
  }

  push(x: number, y: number): void {
    if (!this.config.trail.enabled) return;
    if (!Number.isFinite(this.lastX)) {
      this.drop(x, y);
      this.lastX = x;
      this.lastY = y;
      return;
    }

    const dx = x - this.lastX;
    const dy = y - this.lastY;
    const dist = Math.hypot(dx, dy);
    if (dist < 1.2) return;

    const spacing = Math.max(2, this.config.trail.spacing);
    const steps = Math.max(1, Math.ceil(dist / spacing));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      this.drop(this.lastX + dx * t, this.lastY + dy * t);
    }
    this.lastX = x;
    this.lastY = y;
  }

  clear(): void {
    this.sparks.length = 0;
    this.lift();
    this.view.clear();
  }

  lift(): void {
    this.lastX = Number.NaN;
    this.lastY = Number.NaN;
  }

  update(dt: number, divider: Divider): void {
    const g = this.view;
    g.clear();
    const cfg = this.config.trail;
    if (!cfg.enabled) {
      this.sparks.length = 0;
      this.view.visible = false;
      return;
    }
    this.view.visible = true;

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      spark.age += dt;
      if (spark.age >= spark.life) this.sparks.splice(i, 1);
    }

    const sat = clamp(cfg.saturation, 0, 1);
    const alphaMax = clamp(cfg.maxAlpha, 0, 1);

    for (let i = 0; i < this.sparks.length; i++) {
      const spark = this.sparks[i];
      const u = spark.age / Math.max(0.05, spark.life);
      const remain = 1 - u;
      const fade = remain * remain;
      const above = divider.isAbove(spark.x, spark.y);
      const light = above ? cfg.lightAbove : cfg.lightBelow;
      const color = hslHex(spark.hue, sat, light);
      const alpha = alphaMax * fade;
      const radius = spark.radius * remain;
      if (radius < 0.25) continue;
      if (i > 0) {
        const prev = this.sparks[i - 1];
        g.moveTo(prev.x, prev.y);
        g.lineTo(spark.x, spark.y);
        g.stroke({ width: radius * 1.7, color, alpha: alpha * 0.85, cap: "round", join: "round" });
      }
      g.circle(spark.x, spark.y, radius);
      g.fill({ color, alpha });
    }
  }

  private drop(x: number, y: number): void {
    const cfg = this.config.trail;
    this.hue = (this.hue + cfg.hueSpeed) % 360;
    const spread = Math.max(0, cfg.sizeRandom);
    this.sparks.push({
      x,
      y,
      age: 0,
      life: Math.max(0.15, cfg.life) * (0.85 + Math.random() * 0.3),
      hue: this.hue,
      radius: Math.max(0.6, cfg.size + (Math.random() - 0.5) * spread),
    });
    if (this.sparks.length > 520) this.sparks.splice(0, this.sparks.length - 520);
  }
}

function hslHex(h: number, s: number, l: number): number {
  const sat = clamp(s, 0, 1);
  const lit = clamp(l, 0, 1);
  const c = (1 - Math.abs(2 * lit - 1)) * sat;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) {
    r = c;
    g = x;
  } else if (hp < 2) {
    r = x;
    g = c;
  } else if (hp < 3) {
    g = c;
    b = x;
  } else if (hp < 4) {
    g = x;
    b = c;
  } else if (hp < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const m = lit - c / 2;
  const R = Math.round((r + m) * 255);
  const G = Math.round((g + m) * 255);
  const B = Math.round((b + m) * 255);
  return (R << 16) | (G << 8) | B;
}
